import { createContext, useContext, useEffect, useState } from 'react';
import { EHR } from 'vim-os-js-browser/types';
import { useVimOsContext } from '../useVimOsContext';
import { useDispatch } from 'react-redux';
import { vimOSEncounterSlice } from 'src/modules/ambient_solo/store/slices/encounter';
import { useNavigate } from 'react-router-dom';
import {
  createEncounter,
  getEncounterByEHRID,
} from 'src/modules/ambient_solo/store/actions/encounter';
import { toast } from '@insight-health-ai/roseate/components';
import { VimOSPatientContext } from './PatientContext';
import { useGetPractitionerByEhrId } from 'src/hooks/ehr';
import { useCreatePatient } from 'src/hooks/patient';
import { useAuth } from '@frontegg/react';

interface EncounterContext {
  encounter: EHR.Encounter | undefined;
}

export const VimOSEncounterContext = createContext<EncounterContext>({
  encounter: undefined,
});

export const VimOSEncounterProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const vimOS = useVimOsContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { patient: contextPatient } = useContext(VimOSPatientContext);
  const [encounter, setEncounter] = useState<EHR.Encounter | undefined>(
    undefined
  );
  const [localPatient, setLocalPatient] = useState<EHR.Patient | undefined>(
    undefined
  );
  const [canUpdate, setCanUpdate] = useState(false);
  const [ehrProviderId, setEhrProviderId] = useState<string | undefined>(
    undefined
  );

  const patient = contextPatient || localPatient;

  const { isAuthenticated } = useAuth();
  const { data: practitionerData } = useGetPractitionerByEhrId(ehrProviderId, {
    enabled: isAuthenticated,
  });
  const { mutateAsync: createPatient } = useCreatePatient();

  useEffect(() => {
    const handleEncounterUpdate = (vimEncounter: EHR.Encounter | undefined) => {
      setEncounter(vimEncounter);
      setCanUpdate(!!vimEncounter);

      if (vimEncounter) {
        dispatch(vimOSEncounterSlice(JSON.parse(JSON.stringify(vimEncounter))));
        const providerEhrId = (vimEncounter?.provider as any)?.ehrProviderId;
        if (providerEhrId) {
          setEhrProviderId(providerEhrId);
        }
      }
    };

    const handlePatientUpdate = (vimPatient: EHR.Patient | undefined) => {
      console.log('Patient update received:', vimPatient);
      setLocalPatient(vimPatient);
    };

    vimOS.ehr.subscribe('encounter', handleEncounterUpdate);
    vimOS.ehr.subscribe('patient', handlePatientUpdate);

    return () => {
      vimOS.ehr.unsubscribe('encounter', handleEncounterUpdate);
      vimOS.ehr.unsubscribe('patient', handlePatientUpdate);
    };
  }, [vimOS, dispatch]);

  useEffect(() => {
    if (!encounter || !isAuthenticated) return;

    const ehrEncounterId = encounter?.identifiers?.ehrEncounterId;
    if (!ehrEncounterId) return;

    (dispatch(getEncounterByEHRID(ehrEncounterId) as any) as Promise<any>).then(
      async (ehrEncounter) => {
        if (ehrEncounter?.encounter_id) {
          navigate(`/mobileEncounterDetails/${ehrEncounter.encounter_id}`);
        } else if (
          practitionerData?.practitioner?.practitioner_id &&
          patient?.demographics
        ) {
          try {
            const patientPayload = {
              first_name: patient.demographics.firstName || '',
              last_name: patient.demographics.lastName || '',
              birth_date: patient.demographics.dateOfBirth || '',
              phone:
                patient.contact_info?.mobilePhoneNumber ||
                patient.contact_info?.homePhoneNumber ||
                '',
              email: patient.contact_info?.email || '',
              ehr_id: patient.identifiers?.ehrPatientId || '',
              address: patient.address
                ? {
                    line_1: patient.address.address1 || '',
                    city: patient.address.city || '',
                    state: patient.address.state || '',
                    zip: patient.address.zipCode || '',
                  }
                : undefined,
            };

            const patientResponse = await createPatient(patientPayload);
            const createdPatient = patientResponse.patient;

            const patientName =
              `${createdPatient.last_name || ''}, ${createdPatient.first_name || ''}`.trim() ||
              'Encounter';
            const dateOfService =
              encounter?.basicInformation?.encounterDateOfService;
            const description = dateOfService
              ? `${patientName} - ${dateOfService}`
              : patientName;

            const encounterData = {
              description,
              practitioner_id: practitionerData.practitioner.practitioner_id,
              patient_id: createdPatient.patient_id,
              start_time: new Date().toISOString(),
              ehr_id: ehrEncounterId,
            };

            dispatch(
              createEncounter(encounterData, (createdEncounter: any) => {
                if (createdEncounter?.encounter_id) {
                  console.log('Created encounter:', createdEncounter);
                  navigate(
                    `/mobileEncounterDetails/${createdEncounter.encounter_id}`
                  );
                }
              }) as any
            );
          } catch (error) {
            console.error('Failed to create patient or encounter:', error);
            toast.error('Failed to create encounter');
          }
        } else {
          console.log('Skipping encounter creation - missing data:', {
            hasPractitionerId:
              !!practitionerData?.practitioner?.practitioner_id,
            hasPatientDemographics: !!patient?.demographics,
          });
        }
      }
    );
  }, [
    encounter,
    patient,
    practitionerData,
    dispatch,
    navigate,
    createPatient,
    isAuthenticated,
  ]);

  // Keep session alive with periodic checks
  useEffect(() => {
    if (!vimOS?.ehr?.resourceUpdater || !encounter) {
      return;
    }

    // Ping every 30 seconds to keep the editing session alive
    const keepAliveInterval = setInterval(() => {
      const canUpdateCheck = vimOS.ehr.resourceUpdater.canUpdateEncounter({
        encounterNotes: { generalNotes: true },
      });

      if (!canUpdateCheck.canUpdate) {
        setCanUpdate(false);
        clearInterval(keepAliveInterval);
        toast.warning(
          'EHR editing session expired. Refresh to continue editing.'
        );
      }
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(keepAliveInterval);
    };
  }, [vimOS, encounter, setCanUpdate]);

  return (
    <VimOSEncounterContext.Provider
      value={{
        encounter,
      }}
    >
      {children}
    </VimOSEncounterContext.Provider>
  );
};
